/**
 * The Chats list's pure parts (2026-10-20; edge ember/chats.js is the list
 * itself): what landed between two reads of GET /api/me/chats, whether a
 * banner shows for it, what the back control counts, the row's time and the
 * chat's night — all read on the person's own clock (`clock.zone`; a guest's
 * is the device's). The order of the rows is the server's and is never
 * recomputed here.
 */

import type { ChatLast, ChatRow, ChatsResponse, RoomKind } from './api';

/** The conversation a screen shows: Ember's, or a room's (its thread, or its invitation / wait / closed page). */
export type ChatHere = { kind: 'ember' } | { kind: 'room'; roomId: string } | null;

/** A line that landed in a conversation between two reads, or an invitation that arrived — one per conversation. */
export interface ChatArrival {
  key: string;
  /** What the banner is about (chat_banner's state). */
  state: 'ember' | 'room' | 'invite';
  roomId: string | null;
  /** The room's kind (a 1v1's picture is the other person alone); null for Ember's chat. */
  roomKind: RoomKind | null;
  name: string;
  people: Array<{ name: string; avatar: string }>;
  /** Who said it, for the banner's second line; '' when only one voice speaks there (Ember's chat, an invitation). */
  who: string;
  text: string;
  at: string;
  muted: boolean;
  /** The person's own line (from another device): it moves the row, never raises a banner. */
  mine: boolean;
}

function later(now: ChatLast | null, was: ChatLast | null | undefined): now is ChatLast {
  if (!now) return false;
  if (!was) return true;
  return Date.parse(now.at) > Date.parse(was.at);
}

/**
 * What landed between `prev` and `next`: a conversation whose newest line moved forward (a room that has just
 * appeared counts from nothing), and an invitation that was not there before. `prev` null is the first read after
 * opening — the baseline, so nothing has landed.
 */
export function chatArrivals(prev: ChatsResponse | null, next: ChatsResponse): ChatArrival[] {
  if (!prev) return [];
  const out: ChatArrival[] = [];
  const ember = next.ember.last;
  if (later(ember, prev.ember.last)) {
    out.push({
      key: 'ember', state: 'ember', roomId: null, roomKind: null, name: next.ember.name, people: [],
      who: '', text: ember.text, at: ember.at, muted: false, mine: ember.who === 'You',
    });
  }
  const before = new Map(prev.chats.map((row) => [row.key, row]));
  for (const row of next.chats) {
    const was = before.get(row.key);
    if (row.kind === 'invite') {
      if (!was) {
        out.push({
          key: row.key, state: 'invite', roomId: row.room_id, roomKind: row.room_kind ?? null, name: row.name, people: row.people,
          who: '', text: row.preview, at: row.at || '', muted: row.muted, mine: false,
        });
      }
      continue;
    }
    if (row.kind === 'room' && later(row.last, was?.last)) {
      out.push({
        key: row.key, state: 'room', roomId: row.room_id, roomKind: row.room_kind ?? null, name: row.name, people: row.people,
        who: row.last.who, text: row.last.text, at: row.last.at, muted: row.muted, mine: row.last.who === 'You',
      });
    }
  }
  return out;
}

function isHere(a: ChatArrival, here: ChatHere): boolean {
  if (!here) return false;
  return here.kind === 'ember' ? a.state === 'ember' : a.roomId === here.roomId;
}

/**
 * Where the person is, for the banner (2026-10-26 Riley, the same table on the web and the apps): `off` — a call,
 * sign-in and the telling doors — nothing at all, not even the system's notification while the tab is away; `none`
 * — the home page (its letters fly in, its doors count the rest) and the list (its rows move instead) — no banner;
 * a conversation — everything but itself; the letters — everything but an invitation, which lands there; anywhere
 * else, everything.
 */
export type BannerWhere = { kind: 'off' } | { kind: 'none' } | { kind: 'ember' } | { kind: 'room'; roomId: string } | { kind: 'letters' } | { kind: 'page' };

/** The page's place in that table, from its route (router.ts names) and whether the list stands beside it. */
export function bannerWhereOf(route: { name: string; roomId?: string }, search: URLSearchParams, split: boolean): BannerWhere {
  if (split) return { kind: 'none' };
  switch (route.name) {
    case 'talk':
      // the chat surface; the doors and the call on /talk are a call's or a choice's own moment
      if (search.get('mode') !== 'chat') return { kind: 'off' };
      return search.get('room') ? { kind: 'room', roomId: search.get('room') || '' } : { kind: 'ember' };
    case 'room':
    case 'topic-room':
    case 'friend-intro':
    case 'friend-checkin':
      return route.roomId ? { kind: 'room', roomId: route.roomId } : { kind: 'page' };
    case 'me-letters':
    case 'me-letter-detail':
      return { kind: 'letters' };
    case 'landing': case 'chats':
      return { kind: 'none' };
    case 'auth': case 'handoff': case 'letter-handoff': case 'legal-update': case 'age': case 'me-unlock':
      return { kind: 'off' };
    default:
      return { kind: 'page' };
  }
}

/** A pair's introduction page (New friends): where its invitation, a yes waiting and one that lapsed open (Chats chatHref). */
export function introHref(roomId: string): string {
  return `/friends/intro/${encodeURIComponent(roomId)}`;
}

/**
 * Where an arrival opens: Ember's conversation, a room's thread in it, or an invitation's page — a pair's on its
 * introduction page, as its row on the list (not /rooms/:id, which only turned round to it after a read).
 */
export function arrivalHref(a: ChatArrival): string {
  if (a.state === 'ember' || !a.roomId) return '/talk?mode=chat';
  if (a.state === 'room') return `/talk?mode=chat&room=${encodeURIComponent(a.roomId)}`;
  return a.roomKind === 'pair' ? introHref(a.roomId) : `/rooms/${encodeURIComponent(a.roomId)}`;
}

/**
 * A banner swiped away quiets its conversation for five minutes (10-24 Riley); one that went up by itself quiets
 * nothing — the next line raises it again (2026-10-26 Riley: a minute's quiet in a running conversation read as lost lines).
 */
export const BANNER_QUIET_MS = { shown: 0, swiped: 5 * 60_000 } as const;

/**
 * The one banner to show for a batch of arrivals, or none: never where nothing pops, never for the conversation on
 * screen, a muted room, the person's own line, an invitation on the letters page, or a conversation still quiet after
 * its last banner (`quiet`: key → until when). Of the rest, the newest.
 */
export function bannerFor(arrivals: ChatArrival[], where: BannerWhere, quiet: ReadonlyMap<string, number> = new Map(), now = Date.now()): ChatArrival | null {
  if (where.kind === 'none' || where.kind === 'off') return null;
  const here: ChatHere = where.kind === 'ember' ? { kind: 'ember' } : where.kind === 'room' ? { kind: 'room', roomId: where.roomId } : null;
  let pick: ChatArrival | null = null;
  for (const a of arrivals) {
    if (a.mine || a.muted || isHere(a, here)) continue;
    if (where.kind === 'letters' && a.state === 'invite') continue;
    if ((quiet.get(a.key) ?? 0) > now) continue;
    if (!pick || Date.parse(a.at) > Date.parse(pick.at)) pick = a;
  }
  return pick;
}

/**
 * Whether this person has a list at all: every signed-in person does (2026-10-21 Riley; until then only someone with
 * something past Ember) — Ember's row alone when there is no room, invitation or note. A guest has none: the talk
 * page is their conversation with Ember alone. While the session is still loading, none yet.
 */
export function hasChatList(status: 'loading' | 'authenticated' | 'anonymous'): boolean {
  return status === 'authenticated';
}

/** Everything unread across the person's conversations, Ember's included. */
export function chatsUnread(data: ChatsResponse | null): number {
  if (!data) return 0;
  return data.chats.reduce((n, row) => n + (row.unread || 0), data.ember.unread || 0);
}

/** What the back control counts: unread in every conversation but the one on screen. */
export function unreadElsewhere(data: ChatsResponse | null, here: ChatHere): number {
  if (!data) return 0;
  let n = here?.kind === 'ember' ? 0 : data.ember.unread || 0;
  for (const row of data.chats) {
    if (here?.kind === 'room' && row.room_id === here.roomId) continue;
    n += row.unread || 0;
  }
  return n;
}

/**
 * The list's cards (2026-10-21 founder; mock chats-night-and-fold): under Ember's own card, the rooms the person is in
 * (pinned, then in progress) on one, everything else — an invitation, a yes waiting, Ember looking, a closed room — on
 * the next. The server puts the first kind first, so each card keeps its order.
 */
export function chatCards(rows: ChatRow[]): { rooms: ChatRow[]; rest: ChatRow[] } {
  const inRoom = (r: ChatRow): boolean => r.pinned || r.kind === 'room';
  return { rooms: rows.filter(inRoom), rest: rows.filter((r) => !inRoom(r)) };
}

/** A room's picture ground, 1–4 by its id — the same room the same colour on every client (h·31 + unit over UTF-16). */
export function roomTint(roomId: string | null): number {
  const id = roomId || '';
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 4) + 1;
}

/** The seat whose colour a room line's speaker wears in the list: the thread's (the others in seat order; a name not among them, the first). */
export function speakerSeat(people: Array<{ name: string }>, who: string): number {
  return Math.max(0, people.findIndex((p) => p.name === who));
}

/** The conversation a URL shows: /talk?mode=chat (with ?room= a room's thread), /rooms/:id. */
export function chatHereOf(path: string, search: URLSearchParams): ChatHere {
  if (path === '/talk' && search.get('mode') === 'chat') {
    const room = search.get('room');
    return room ? { kind: 'room', roomId: room } : { kind: 'ember' };
  }
  const m = /^\/rooms\/([^/]+)\/?$/.exec(path);
  if (m) {
    try { return { kind: 'room', roomId: decodeURIComponent(m[1]) }; } catch { return { kind: 'room', roomId: m[1] }; }
  }
  return null;
}

/** Year, month, day and hour of a moment on a clock ('' = the device's). */
function partsOn(at: Date, zone: string): { y: number; m: number; d: number; h: number } {
  const fmt = (timeZone: string | undefined) => new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23',
  }).formatToParts(at);
  let parts: Intl.DateTimeFormatPart[];
  try { parts = fmt(zone || undefined); } catch { parts = fmt(undefined); }
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24 };
}

/** Night on the person's clock: 20:00 to 06:00. Time only — the OS's dark mode has no say. */
export function isNightAt(at: Date, zone: string): boolean {
  const { h } = partsOn(at, zone);
  return h >= 20 || h < 6;
}

export type ChatSkin = 'sand' | 'night';

export function chatSkinAt(at: Date, zone: string): ChatSkin {
  return isNightAt(at, zone) ? 'night' : 'sand';
}

function formatOn(at: Date, zone: string, options: Intl.DateTimeFormatOptions): string {
  try { return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone || undefined }).format(at); } catch { return new Intl.DateTimeFormat('en-US', options).format(at); }
}

/** A date as the list says it: "Oct 12". */
export function chatDate(at: string, zone: string): string {
  return formatOn(new Date(at), zone, { month: 'short', day: 'numeric' });
}

/** A row's time on the person's clock: today "9:40 AM", yesterday "Yesterday", else "Oct 12". */
export function chatRowTime(at: string | null, zone: string, now: Date = new Date()): string {
  if (!at) return '';
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return '';
  const a = partsOn(when, zone);
  const t = partsOn(now, zone);
  if (a.y === t.y && a.m === t.m && a.d === t.d) return formatOn(when, zone, { hour: 'numeric', minute: '2-digit' });
  // The calendar day before today on that clock (a date, not 24 hours: a DST day is 23 or 25).
  const y = new Date(Date.UTC(t.y, t.m - 1, t.d - 1));
  if (a.y === y.getUTCFullYear() && a.m === y.getUTCMonth() + 1 && a.d === y.getUTCDate()) return 'Yesterday';
  return chatDate(at, zone);
}
